/**
 * BigFit Theme — cart-drawer.js
 * AJAX cart: quantity updates, remove items, refresh drawer HTML
 */

(function () {
  'use strict';

  /* ────────────────────────────────────────────
     Helpers
  ──────────────────────────────────────────── */
  function setLoading(active) {
    const el = document.getElementById('cart-drawer-loading');
    if (!el) return;
    el.classList.toggle('is-active', active);
  }

  function money(cents) {
    const fmt = (window.Shopify && window.Shopify.money_format) || '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    return fmt.replace('{{amount}}', amount).replace('{{amount_no_decimals}}', Math.round(cents / 100));
  }

  /* ────────────────────────────────────────────
     Refresh cart drawer contents via AJAX
  ──────────────────────────────────────────── */
  function refreshCart() {
    setLoading(true);
    return fetch('/cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        renderCart(cart);
        if (window.updateCartCount) window.updateCartCount(cart.item_count);
        return cart;
      })
      .catch(function (err) {
        console.error('Cart refresh error:', err);
      })
      .finally(function () {
        setLoading(false);
      });
  }

  /* ────────────────────────────────────────────
     Render cart items into the drawer
  ──────────────────────────────────────────── */
  function renderCart(cart) {
    const body       = document.getElementById('cart-drawer-body');
    const countEl    = document.getElementById('cart-drawer-count');
    if (!body) return;

    if (countEl) countEl.textContent = '(' + cart.item_count + ')';

    if (cart.item_count === 0) {
      body.innerHTML = emptyStateHTML();
      return;
    }

    let itemsHTML = '<div class="cart-drawer__items" id="cart-items-list">';
    cart.items.forEach(function (item, index) {
      const lineNum  = index + 1;
      const imgSrc   = item.image
        ? item.image.replace('.jpg', '_176x176.jpg').replace('.png', '_176x176.png').replace('.webp', '_176x176.webp')
        : '';
      const varTitle = item.variant_title && item.variant_title !== 'Default Title'
        ? '<p class="cart-item__variant">' + escHtml(item.variant_title) + '</p>'
        : '';

      itemsHTML += [
        '<div class="cart-item" data-line="' + lineNum + '">',
          imgSrc
            ? '<img class="cart-item__img" src="' + imgSrc + '" alt="' + escHtml(item.title) + '" width="88" height="88" loading="lazy">'
            : '<div class="cart-item__img" style="background:var(--color-gray-100)"></div>',
          '<div class="cart-item__details">',
            '<p class="cart-item__title">' + escHtml(item.product_title) + '</p>',
            varTitle,
            '<div class="cart-item__row">',
              '<div class="cart-item__qty" role="group" aria-label="Quantity">',
                '<button class="cart-item__qty-btn" data-action="decrease" data-line="' + lineNum + '" aria-label="Decrease quantity">&minus;</button>',
                '<span class="cart-item__qty-value" data-qty="' + item.quantity + '">' + item.quantity + '</span>',
                '<button class="cart-item__qty-btn" data-action="increase" data-line="' + lineNum + '" aria-label="Increase quantity">+</button>',
              '</div>',
              '<p class="cart-item__price">' + money(item.final_line_price) + '</p>',
            '</div>',
            '<button class="cart-item__remove" data-action="remove" data-line="' + lineNum + '" aria-label="Remove ' + escHtml(item.product_title) + '">Remove</button>',
          '</div>',
        '</div>'
      ].join('');
    });
    itemsHTML += '</div>';

    const footerHTML = [
      '<div class="cart-drawer__footer" id="cart-footer">',
        '<div class="cart-drawer__subtotal">',
          '<span class="cart-drawer__subtotal-label">Subtotal</span>',
          '<span class="cart-drawer__subtotal-value" id="cart-subtotal">' + money(cart.total_price) + '</span>',
        '</div>',
        '<p class="cart-drawer__shipping-note">Taxes and shipping calculated at checkout.</p>',
        '<div class="cart-drawer__checkout">',
          '<a href="/checkout" class="btn btn-primary btn-full">Checkout</a>',
          '<a href="/collections/all" class="btn btn-outline-gold btn-full btn-sm">Continue Shopping</a>',
        '</div>',
      '</div>'
    ].join('');

    body.innerHTML = itemsHTML + footerHTML;
  }

  function emptyStateHTML() {
    return [
      '<div class="cart-drawer__empty" id="cart-empty-state">',
        '<svg class="cart-drawer__empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">',
          '<path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>',
          '<line x1="3" y1="6" x2="21" y2="6"/>',
          '<path d="M16 10a4 4 0 0 1-8 0"/>',
        '</svg>',
        '<p class="cart-drawer__empty-title">Your cart is empty</p>',
        '<p style="color:var(--color-gray-400);font-size:var(--font-size-sm)">Find the perfect cap for your head size.</p>',
        '<a href="/collections/all" class="btn btn-primary">Shop Now</a>',
      '</div>'
    ].join('');
  }

  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* ────────────────────────────────────────────
     Update line item quantity
  ──────────────────────────────────────────── */
  function updateLineItem(line, quantity) {
    setLoading(true);
    const updates = {};
    updates[line] = quantity;

    return fetch(window.routes.cart_change_url || '/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Cart update failed');
        return res.json();
      })
      .then(function (cart) {
        renderCart(cart);
        if (window.updateCartCount) window.updateCartCount(cart.item_count);
      })
      .catch(function (err) {
        console.error('Line item update error:', err);
      })
      .finally(function () {
        setLoading(false);
      });
  }

  /* ────────────────────────────────────────────
     Event delegation: qty +/- and remove
  ──────────────────────────────────────────── */
  document.addEventListener('click', function (e) {
    // Quantity decrease
    const qtyBtn = e.target.closest('.cart-item__qty-btn');
    if (qtyBtn) {
      e.preventDefault();
      const line    = parseInt(qtyBtn.dataset.line, 10);
      const action  = qtyBtn.dataset.action;
      const qtyEl   = qtyBtn.closest('.cart-item__qty').querySelector('.cart-item__qty-value');
      const current = parseInt(qtyEl ? qtyEl.dataset.qty : 1, 10);
      const newQty  = action === 'increase' ? current + 1 : Math.max(0, current - 1);
      updateLineItem(line, newQty);
      return;
    }

    // Remove item
    const removeBtn = e.target.closest('.cart-item__remove');
    if (removeBtn) {
      e.preventDefault();
      const line = parseInt(removeBtn.dataset.line, 10);
      updateLineItem(line, 0);
      return;
    }
  });

  /* ────────────────────────────────────────────
     Listen for cart:updated event (from theme.js quick-add)
  ──────────────────────────────────────────── */
  window.addEventListener('cart:updated', function (e) {
    if (e.detail && e.detail.cart) {
      renderCart(e.detail.cart);
    }
  });

})();
